import { Modal } from 'antd';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
  100% {
    transform: scale(0.8);
    opacity: 1;
  }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

const progressFill = keyframes`
  0% {
    width: 0%;
  }
  100% {
    width: var(--progress-width, 0%);
  }
`;

export const ExportLoadingModalStyle = styled(Modal)`
  .ant-modal-content {
    background: ${({ theme }) => theme.background.main};
    border-radius: 20px;
    padding: 0;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(255, 138, 50, 0.15);
  }

  .ant-modal-body {
    padding: 0;
  }

  .export-loading-container {
    padding: 40px 30px;
    text-align: center;
    color: #FF8A32;
    position: relative;
    background: #ffffff;
  }

  .loading-animation {
    position: relative;
    margin-bottom: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .spinner {
    position: relative;
    width: 80px;
    height: 80px;
  }

  .spinner-inner {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 3px solid transparent;
    border-top: 3px solid #FF8A32;
    border-right: 3px solid #FF8A32;
    animation: ${spin} 1.5s linear infinite;
  }

  .spinner-dot {
    position: absolute;
    width: 8px;
    height: 8px;
    background: #FF8A32;
    border-radius: 50%;
    animation: ${bounce} 1.4s ease-in-out infinite both;

    &:nth-child(1) {
      top: 10px;
      left: 50%;
      transform: translateX(-50%);
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      top: 50%;
      right: 10px;
      transform: translateY(-50%);
      animation-delay: -0.16s;
    }

    &:nth-child(3) {
      bottom: 10px;
      left: 50%;
      transform: translateX(-50%);
      animation-delay: 0s;
    }

    &:nth-child(4) {
      top: 50%;
      left: 10px;
      transform: translateY(-50%);
      animation-delay: 0.16s;
    }
  }

  .pulse-ring {
    position: absolute;
    width: 100px;
    height: 100px;
    border: 2px solid rgba(255, 138, 50, 0.4);
    border-radius: 50%;
    animation: ${pulse} 2s ease-in-out infinite;
  }

  .export-content {
    position: relative;
    z-index: 2;
  }

  .export-icon {
    font-size: 48px;
    margin-bottom: 20px;
    animation: ${bounce} 2s ease-in-out infinite;
  }

  .export-title {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 10px 0;
    color: #FF8A32;
  }

  .export-description {
    font-size: 16px;
    margin: 0 0 30px 0;
    color: #FF8A32;
    opacity: 0.8;
    line-height: 1.5;
  }

  .progress-container {
    margin-bottom: 20px;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
    background: rgba(255, 138, 50, 0.15);
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 10px;
    position: relative;
  }

  .progress-fill {
    height: 100%;
    background: #FF8A32;
    border-radius: 10px;
    position: relative;
    transition: width 0.3s ease;
    animation: ${progressFill} 2s ease-out;
    
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.4),
        transparent
      );
      animation: ${shimmer} 2s infinite;
    }
  }

  .progress-text {
    font-size: 14px;
    font-weight: 500;
    color: #FF8A32;
    opacity: 0.9;
  }

  .loading-dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 20px;

    span {
      width: 8px;
      height: 8px;
      background: #FF8A32;
      border-radius: 50%;
      animation: ${bounce} 1.4s ease-in-out infinite both;

      &:nth-child(1) {
        animation-delay: -0.32s;
      }

      &:nth-child(2) {
        animation-delay: -0.16s;
      }

      &:nth-child(3) {
        animation-delay: 0s;
      }
    }
  }

  /* Dark theme support */
  @media (prefers-color-scheme: dark) {
    .ant-modal-content {
      background: #ffffff;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
      border: 1px solid rgba(255, 138, 50, 0.15);
    }

    .export-loading-container {
      background: #ffffff;
      color: #FF8A32;
    }

    .export-title {
      color: #FF8A32;
    }

    .export-description {
      color: #FF8A32;
    }

    .progress-text {
      color: #FF8A32;
    }
  }

  /* Responsive design */
  @media (max-width: 480px) {
    .export-loading-container {
      padding: 30px 20px;
    }

    .export-title {
      font-size: 20px;
    }

    .export-description {
      font-size: 14px;
    }

    .spinner {
      width: 60px;
      height: 60px;
    }

    .pulse-ring {
      width: 80px;
      height: 80px;
    }
  }
`;
